import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from 'antd';
import { TrophyOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const LoadingGame = ({ onGameComplete }) => {
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(30);
    const [gameStarted, setGameStarted] = useState(false);
    const [gameEnded, setGameEnded] = useState(false);
    const [fallingEmojis, setFallingEmojis] = useState([]);
    const [animations, setAnimations] = useState([]); // For catch/blast animations
    const [combo, setCombo] = useState(0);
    const [multiplier, setMultiplier] = useState(1);
    const [powerUpActive, setPowerUpActive] = useState(null);
    const [powerUpTimeLeft, setPowerUpTimeLeft] = useState(0);
    const [caughtCount, setCaughtCount] = useState(0);
    const [gameOverAnimation, setGameOverAnimation] = useState(false);
    const gameAreaRef = useRef(null);
    const gameIntervalRef = useRef(null);
    const emojiIntervalRef = useRef(null);
    const powerUpIntervalRef = useRef(null);
    const comboTimeoutRef = useRef(null);
    const explosionAudioRef = useRef(null);
    const catchAudioRef = useRef(null);
    const backgroundAudioRef = useRef(null);

    // Different emoji types with different values
    const emojiTypes = {
        normal: ['🐂', '🐃', '🐏', '🐑', '🐐', '🐎', '🫏', '🐔', '🐓', '🐣', '🦆', '🦃', '🦙'],
        rare: ['🦄', '🐉', '🦁', '🐯', '🐻', '🐼'],
        special: ['⭐', '💎', '🎁', '🏆'],
        bad: ['💣', '☠️', '⚡']
    };

    // Initialize audio files
    useEffect(() => {
        explosionAudioRef.current = new Audio('/sounds/explosion.mp3');
        explosionAudioRef.current.volume = 0.7; // Set volume to 70%
        
        catchAudioRef.current = new Audio('/sounds/catch.mp3');
        catchAudioRef.current.volume = 0.6; // Set volume to 60%
        
        backgroundAudioRef.current = new Audio('/sounds/catch-background.mp3');
        backgroundAudioRef.current.volume = 0.4; // Set volume to 40% for background music
        backgroundAudioRef.current.loop = true; // Loop the background music
        
        return () => {
            if (explosionAudioRef.current) {
                explosionAudioRef.current.pause();
                explosionAudioRef.current = null;
            }
            if (catchAudioRef.current) {
                catchAudioRef.current.pause();
                catchAudioRef.current = null;
            }
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current = null;
            }
        };
    }, []);

    const startGame = () => {
        setGameStarted(true);
        setGameEnded(false);
        setScore(0);
        setTimeLeft(30);
        setFallingEmojis([]);
        setCombo(0);
        setMultiplier(1);
        setPowerUpActive(null);
        setPowerUpTimeLeft(0);
        setCaughtCount(0);
        setGameOverAnimation(false);
        
        // Start background music
        if (backgroundAudioRef.current) {
            try {
                backgroundAudioRef.current.currentTime = 0; // Reset to start
                backgroundAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play background music:', error);
                });
            } catch (error) {
                console.log('Error playing background music:', error);
            }
        }
    };

    const catchEmoji = (id, event, emoji, type) => {
        // Get exact click position relative to game area
        let x, y;
        if (event && gameAreaRef.current) {
            const rect = gameAreaRef.current.getBoundingClientRect();
            // Get click position relative to the game area container
            x = event.clientX - rect.left;
            y = event.clientY - rect.top;
        } else {
            // Fallback to emoji position if event not available
            const emojiObj = fallingEmojis.find(e => e.id === id);
            if (emojiObj && gameAreaRef.current) {
                const rect = gameAreaRef.current.getBoundingClientRect();
                // Convert percentage to pixels
                x = (emojiObj.x / 100) * rect.width;
                // Convert percentage Y to actual pixel position
                y = (emojiObj.y / 100) * rect.height;
            } else {
                return;
            }
        }
        // Handle bad emojis - game over!
        if (type === 'bad') {
            setCombo(0);
            setFallingEmojis(prev => prev.filter(e => e.id !== id));
            
            // Play explosion sound
            if (explosionAudioRef.current) {
                try {
                    explosionAudioRef.current.currentTime = 0; // Reset to start
                    explosionAudioRef.current.play().catch(error => {
                        // Handle autoplay restrictions
                        console.log('Could not play explosion sound:', error);
                    });
                } catch (error) {
                    console.log('Error playing explosion sound:', error);
                }
            }
            
            // Trigger screen shake and red blinking immediately (emergency effect)
            setGameOverAnimation(true);
            
            // Create explosion animation
            const animationId = Date.now();
            setAnimations(prev => [...prev, {
                id: animationId,
                x: x,
                y: y,
                emoji: '💥',
                type: 'explosion'
            }]);
            
            // Stop shake/blink animation after 1.5 seconds
            setTimeout(() => {
                setGameOverAnimation(false);
            }, 1500);
            
            // Game over after explosion animation
            setTimeout(() => {
                setGameEnded(true);
                setGameStarted(false);
                
                // Stop background music
                if (backgroundAudioRef.current) {
                    backgroundAudioRef.current.pause();
                    backgroundAudioRef.current.currentTime = 0;
                }
                
                if (onGameComplete) {
                    setTimeout(() => onGameComplete(score), 500);
                }
            }, 800);
            
            setTimeout(() => {
                setAnimations(prev => prev.filter(anim => anim.id !== animationId));
            }, 800);
            return;
        }
        
        // Play catch sound for good emojis
        if (catchAudioRef.current) {
            try {
                catchAudioRef.current.currentTime = 0; // Reset to start
                catchAudioRef.current.play().catch(error => {
                    // Handle autoplay restrictions
                    console.log('Could not play catch sound:', error);
                });
            } catch (error) {
                console.log('Error playing catch sound:', error);
            }
        }
        
        // Calculate points based on type
        let points = 10;
        if (type === 'rare') points = 25;
        else if (type === 'special') {
            if (emoji === '⭐') {
                // Star gives double points power-up
                setPowerUpActive('double');
                setPowerUpTimeLeft(10);
                points = 15;
            } else if (emoji === '💎') {
                points = 50;
            } else if (emoji === '🎁') {
                points = 30;
            } else if (emoji === '🏆') {
                points = 100;
            }
        }
        
        // Apply multiplier (combo multiplier + power-up multiplier)
        let activeMultiplier = multiplier;
        if (powerUpActive === 'double') {
            activeMultiplier = multiplier * 2; // Double points power-up doubles everything
        }
        const finalPoints = points * activeMultiplier;
        
        // Remove the emoji
        setFallingEmojis(prev => prev.filter(e => e.id !== id));
        setScore(prev => prev + finalPoints);
        setCaughtCount(prev => prev + 1);
        
        // Increase combo
        const newCombo = combo + 1;
        setCombo(newCombo);
        
        // Combo multiplier (every 5 catches = 2x, every 10 = 3x)
        if (newCombo % 5 === 0 && newCombo > 0) {
            setMultiplier(Math.min(3, Math.floor(newCombo / 5) + 1));
        }
        
        // Reset combo after 2 seconds of no catches
        if (comboTimeoutRef.current) {
            clearTimeout(comboTimeoutRef.current);
        }
        comboTimeoutRef.current = setTimeout(() => {
            setCombo(0);
            setMultiplier(1);
        }, 2000);
        
        // Create enhanced blast/catch animation
        const animationId = Date.now();
        setAnimations(prev => [...prev, {
            id: animationId,
            x: x,
            y: y,
            emoji: emoji,
            type: 'blast',
            points: finalPoints,
            timestamp: Date.now()
        }]);
        
        // Remove animation after it completes
        setTimeout(() => {
            setAnimations(prev => prev.filter(anim => anim.id !== animationId));
        }, 1200);
    };

    useEffect(() => {
        if (!gameStarted || gameEnded) return;

        // Timer countdown
        gameIntervalRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    setGameOverAnimation(true);
                    setGameEnded(true);
                    setGameStarted(false);
                    
                    // Stop background music
                    if (backgroundAudioRef.current) {
                        backgroundAudioRef.current.pause();
                        backgroundAudioRef.current.currentTime = 0;
                    }
                    
                    // Stop animation after 1 second
                    setTimeout(() => {
                        setGameOverAnimation(false);
                    }, 1000);
                    
                    if (onGameComplete) {
                        setTimeout(() => onGameComplete(score), 1500);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // Spawn falling emojis with variety
        emojiIntervalRef.current = setInterval(() => {
            const rand = Math.random();
            let emojiType, emoji, speed;
            
            if (rand < 0.12) {
                // 12% chance for bad emoji (increased from 5%)
                emojiType = 'bad';
                // Make lightning appear more frequently (50% chance when bad emoji is selected)
                const badRand = Math.random();
                if (badRand < 0.5) {
                    emoji = '⚡'; // Lightning - 50% of bad emojis (most common)
                } else if (badRand < 0.75) {
                    emoji = '☠️'; // Skeleton - 25% of bad emojis
                } else {
                    emoji = '💣'; // Bomb - 25% of bad emojis
                }
                // Make all bad emojis fall slower
                if (emoji === '💣') {
                    speed = 0.3 + Math.random() * 0.3; // Slow for bomb
                } else if (emoji === '☠️') {
                    speed = 0.4 + Math.random() * 0.4; // Slow for skeleton
                } else {
                    speed = 0.5 + Math.random() * 0.4; // Slow for lightning (was fast)
                }
            } else if (rand < 0.22) {
                // 10% chance for special
                emojiType = 'special';
                emoji = emojiTypes.special[Math.floor(Math.random() * emojiTypes.special.length)];
                speed = 0.3 + Math.random() * 0.4; // Slower
            } else if (rand < 0.30) {
                // 15% chance for rare
                emojiType = 'rare';
                emoji = emojiTypes.rare[Math.floor(Math.random() * emojiTypes.rare.length)];
                speed = 0.6 + Math.random() * 0.5;
            } else {
                // 70% normal
                emojiType = 'normal';
                emoji = emojiTypes.normal[Math.floor(Math.random() * emojiTypes.normal.length)];
                speed = 0.5 + Math.random() * 1;
            }
            
            const randomX = Math.random() * 90; // 0-90% of width
            
            setFallingEmojis(prev => [
                ...prev,
                {
                    id: Date.now() + Math.random(),
                    emoji: emoji,
                    type: emojiType,
                    x: randomX,
                    y: 0,
                    speed: speed
                }
            ]);
        }, 800); // Spawn faster for more action

        // Power-up timer
        if (powerUpActive) {
            powerUpIntervalRef.current = setInterval(() => {
                setPowerUpTimeLeft(prev => {
                    if (prev <= 1) {
                        setPowerUpActive(null);
                        setMultiplier(1);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (gameIntervalRef.current) clearInterval(gameIntervalRef.current);
            if (emojiIntervalRef.current) clearInterval(emojiIntervalRef.current);
            if (powerUpIntervalRef.current) clearInterval(powerUpIntervalRef.current);
        };
    }, [gameStarted, gameEnded, score, powerUpActive]);

    // Animate falling emojis
    useEffect(() => {
        if (!gameStarted || gameEnded) return;

        const speedMultiplier = powerUpActive === 'slow' ? 0.5 : 1;
        const animationFrame = setInterval(() => {
            setFallingEmojis(prev => {
                return prev
                    .map(emoji => ({
                        ...emoji,
                        y: emoji.y + (emoji.speed * speedMultiplier)
                    }))
                    .filter(emoji => emoji.y < 100); // Remove emojis that reach bottom
            });
        }, 16); // ~60fps

        return () => clearInterval(animationFrame);
    }, [gameStarted, gameEnded, powerUpActive]);

    const getGameMessage = () => {
        if (score >= 500) return "🏆 Legendary! You're a master!";
        if (score >= 300) return "⭐ Amazing! Incredible skills!";
        if (score >= 200) return "🔥 Excellent! Great job!";
        if (score >= 100) return "👍 Good! Keep it up!";
        if (score >= 50) return "🎯 Not bad! Try harder!";
        return "💪 Keep practicing!";
    };

    return (
        <div style={{
            width: '100%',
            margin: '0 auto',
            padding: '0'
        }}>
            {!gameStarted && !gameEnded && (
                <div style={{
                    textAlign: 'center',
                    padding: '0',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(24, 144, 255, 0.2)',
                    overflow: 'hidden'
                }}>
                    {/* Game Preview Image */}
                    <div style={{
                        width: '100%',
                        height: '400px',
                        background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 30%, #98D8C8 70%, #90EE90 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <img 
                            src="/assets/images/catch-emoji.jpeg" 
                            alt="Emoji Game - Catch Emojis"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center'
                            }}
                            onError={(e) => {
                                // Fallback if image doesn't exist
                                e.target.style.display = 'none';
                            }}
                        />
                      
                    </div>
                    
                    {/* Start Button Section */}
                    <div style={{
                        padding: '20px 16px',
                        background: 'rgba(255, 255, 255, 0.95)'
                    }}>
                        <Title level={5} style={{ marginBottom: '10px', color: '#1890ff', fontSize: '16px' }}>
                            🎮 Quick Game
                        </Title>
                        <Text style={{ 
                            fontSize: '13px', 
                            color: '#666',
                            display: 'block',
                            marginBottom: '12px'
                        }}>
                            Catch falling emojis! 30 seconds to score!
                        </Text>
                        <Button
                            type="primary"
                            size="middle"
                            icon={<TrophyOutlined />}
                            onClick={startGame}
                            style={{
                                height: '36px',
                                fontSize: '13px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                padding: '0 20px',
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                border: 'none',
                                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                            }}
                        >
                            Start
                        </Button>
                    </div>
                </div>
            )}

            {gameStarted && !gameEnded && (
                <div style={{ overflow: 'hidden' }}>
                    {/* Game Stats */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '12px 16px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        border: '1px solid rgba(24, 144, 255, 0.2)'
                    }}>
                        <div>
                            <Text style={{ fontSize: '14px', color: '#666', display: 'block', fontWeight: 600 }}>Score</Text>
                            <Text style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                                {score}
                            </Text>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <Text style={{ fontSize: '14px', color: '#666', display: 'block', fontWeight: 600 }}>Time</Text>
                            <Text style={{ 
                                fontSize: '28px', 
                                fontWeight: 'bold', 
                                color: timeLeft <= 10 ? '#ff4d4f' : '#52c41a'
                            }}>
                                {timeLeft}s
                            </Text>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <Text style={{ fontSize: '14px', color: '#666', display: 'block', fontWeight: 600 }}>Caught</Text>
                            <Text style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                                {caughtCount}
                            </Text>
                        </div>
                        {combo > 0 && (
                            <div style={{ textAlign: 'center' }}>
                                <Text style={{ fontSize: '14px', color: '#666', display: 'block', fontWeight: 600 }}>Combo</Text>
                                <Text style={{ 
                                    fontSize: '28px', 
                                    fontWeight: 'bold', 
                                    color: combo >= 10 ? '#ff4d4f' : combo >= 5 ? '#fa8c16' : '#52c41a'
                                }}>
                                    {combo}x 🔥
                                </Text>
                            </div>
                        )}
                        {powerUpActive && (
                            <div style={{ textAlign: 'right' }}>
                                <Text style={{ 
                                    fontSize: '14px', 
                                    color: '#666', 
                                    display: 'block', 
                                    fontWeight: 600,
                                    animation: 'powerup-pulse 1s ease-in-out infinite'
                                }}>
                                    {powerUpActive === 'double' ? '2x Points' : 'Slow Motion'}
                                </Text>
                                <Text style={{ 
                                    fontSize: '28px', 
                                    fontWeight: 'bold', 
                                    color: '#1890ff',
                                    animation: 'powerup-pulse 1s ease-in-out infinite'
                                }}>
                                    {powerUpTimeLeft}s ⚡
                                </Text>
                            </div>
                        )}
                    </div>

                    {/* Game Area */}
                    <div
                        ref={gameAreaRef}
                        className="game-area-container"
                        style={{
                            position: 'relative',
                            width: '100%',
                            height: '400px',
                            maxHeight: '400px',
                            background: gameOverAnimation 
                                ? 'linear-gradient(180deg, #ff6b6b 0%, #ff8787 50%, #ff9999 100%)'
                                : 'linear-gradient(180deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: gameOverAnimation 
                                ? '3px solid rgba(255, 0, 0, 0.8)'
                                : '3px solid rgba(24, 144, 255, 0.3)',
                            boxShadow: gameOverAnimation 
                                ? 'inset 0 2px 10px rgba(255, 0, 0, 0.5)'
                                : 'inset 0 2px 10px rgba(0, 0, 0, 0.1)',
                            cursor: 'crosshair',
                            flexShrink: 0,
                            animation: gameOverAnimation ? 'screen-shake 1.5s ease-in-out' : 'none'
                        }}
                    >
                        {/* Red Flash Overlay - Emergency Effect */}
                        {gameOverAnimation && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(255, 0, 0, 0.5)',
                                zIndex: 200,
                                animation: 'red-blink 1.5s ease-in-out infinite',
                                pointerEvents: 'none'
                            }} />
                        )}

                        {/* Obstacle Warning Indicator */}
                        <div style={{
                            position: 'absolute',
                            top: '10px',
                            left: '10px',
                            padding: '8px 12px',
                            zIndex: 15,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                       
                            {emojiTypes.bad.map((badEmoji, index) => (
                                <span
                                    key={index}
                                    style={{
                                        fontSize: '28px',
                                        filter: 'drop-shadow(0 0 6px rgba(255, 0, 0, 0.8))',
                                        animation: 'emoji-shake 0.8s ease-in-out infinite',
                                        animationDelay: `${index * 0.2}s`,
                                    }}
                                >
                                    {badEmoji}
                                </span>
                            ))}
                        </div>

                        {/* Falling Emojis */}
                        {fallingEmojis.map(emoji => {
                            if (!gameAreaRef.current) return null;
                            
                            // Different styling based on type
                            let emojiSize = '60px';
                            let emojiFilter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
                            
                            if (emoji.type === 'rare') {
                                emojiSize = '70px';
                                emojiFilter = 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.8)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
                            } else if (emoji.type === 'special') {
                                emojiSize = '65px';
                                emojiFilter = 'drop-shadow(0 0 20px rgba(24, 144, 255, 0.9)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
                            } else if (emoji.type === 'bad') {
                                emojiSize = '55px';
                                emojiFilter = 'drop-shadow(0 0 10px rgba(255, 0, 0, 0.8)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))';
                            }
                            
                            return (
                                <div
                                    key={emoji.id}
                                    onClick={(e) => catchEmoji(emoji.id, e, emoji.emoji, emoji.type)}
                                    className="emoji-clickable"
                                    style={{
                                        position: 'absolute',
                                        left: `${emoji.x}%`,
                                        top: `${emoji.y}%`,
                                        fontSize: emojiSize,
                                        cursor: emoji.type === 'bad' ? 'not-allowed' : 'pointer',
                                        transform: 'translate(-50%, -50%)',
                                        transition: 'transform 0.1s ease',
                                        zIndex: 10,
                                        filter: emojiFilter,
                                        animation: emoji.type === 'bad' 
                                            ? 'emoji-shake 0.5s ease-in-out infinite' 
                                            : 'emoji-float 2s ease-in-out infinite',
                                        userSelect: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (emoji.type !== 'bad') {
                                            e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                                    }}
                                >
                                    {emoji.emoji}
                                </div>
                            );
                        })}
                        
                        {/* Blast/Catch Animations */}
                        {animations.map(anim => {
                            // Ensure coordinates are within bounds
                            const x = Math.max(0, Math.min(anim.x, gameAreaRef.current?.offsetWidth || 400));
                            const y = Math.max(0, Math.min(anim.y, gameAreaRef.current?.offsetHeight || 400));
                            
                            return (
                            <div
                                key={anim.id}
                                className="catch-animation"
                                style={{
                                    position: 'absolute',
                                    left: `${x}px`,
                                    top: `${y}px`,
                                    zIndex: 100,
                                    pointerEvents: 'none',
                                    transform: 'translate(-50%, -50%)',
                                    transformOrigin: 'center center'
                                }}
                            >
                                {anim.type === 'explosion' ? (
                                    <div className="explosion-blast">
                                        <div className="explosion-emoji">💥</div>
                                        <div className="explosion-ring"></div>
                                        <div className="explosion-particles">
                                            <span className="exp-particle-1">🔥</span>
                                            <span className="exp-particle-2">💥</span>
                                            <span className="exp-particle-3">⚡</span>
                                            <span className="exp-particle-4">💥</span>
                                            <span className="exp-particle-5">🔥</span>
                                            <span className="exp-particle-6">⚡</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="catch-blast">
                                        <div className="catch-emoji">{anim.emoji}</div>
                                        <div className="catch-particles">
                                            <span className="particle-1">✨</span>
                                            <span className="particle-2">⭐</span>
                                            <span className="particle-3">💫</span>
                                            <span className="particle-4">🌟</span>
                                            <span className="particle-5">✨</span>
                                            <span className="particle-6">⭐</span>
                                            <span className="particle-7">✨</span>
                                            <span className="particle-8">⭐</span>
                                        </div>
                                        <div className="burst-ring"></div>
                                        {anim.points && (
                                            <div className="points-popup" style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                fontSize: '28px',
                                                fontWeight: 'bold',
                                                color: '#52c41a',
                                                textShadow: '2px 2px 6px rgba(0, 0, 0, 0.8), 0 0 10px rgba(82, 196, 26, 0.6)',
                                                animation: 'points-float 1.2s ease-out forwards',
                                                zIndex: 101
                                            }}>
                                                +{anim.points}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })}

                    </div>

                </div>
            )}

            {gameEnded && (
                <div style={{
                    textAlign: 'center',
                    padding: '20px 16px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(24, 144, 255, 0.2)'
                }}>
                    <TrophyOutlined style={{ fontSize: '40px', color: '#ffd700', marginBottom: '10px' }} />
                    <Title level={5} style={{ marginBottom: '6px', color: '#1890ff', fontSize: '16px' }}>
                        Game Over!
                    </Title>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a', display: 'block', marginBottom: '6px' }}>
                        Score: {score}
                    </Text>
                    <Text style={{ fontSize: '13px', color: '#666', display: 'block', marginBottom: '12px' }}>
                        {getGameMessage()}
                    </Text>
                    <Button
                        type="primary"
                        size="middle"
                        icon={<ReloadOutlined />}
                        onClick={startGame}
                        style={{
                            height: '36px',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 20px',
                            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                            border: 'none',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                        }}
                    >
                        Play Again
                    </Button>
                </div>
            )}

            <style jsx>{`
                @keyframes emoji-float {
                    0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
                    25% { transform: translate(-50%, -50%) rotate(-5deg); }
                    75% { transform: translate(-50%, -50%) rotate(5deg); }
                }
                
                @keyframes emoji-shake {
                    0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
                    25% { transform: translate(-50%, -50%) rotate(-10deg); }
                    75% { transform: translate(-50%, -50%) rotate(10deg); }
                }
                
                @keyframes powerup-pulse {
                    0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.8;
                        transform: scale(1.05);
                    }
                }
                
                @keyframes screen-shake {
                    0%, 100% {
                        transform: translateX(0) translateY(0);
                    }
                    5%, 15%, 25%, 35%, 45%, 55%, 65%, 75%, 85%, 95% {
                        transform: translateX(-15px) translateY(-8px) rotate(-2deg);
                    }
                    10%, 20%, 30%, 40%, 50%, 60%, 70%, 80%, 90% {
                        transform: translateX(15px) translateY(8px) rotate(2deg);
                    }
                }
                
                @keyframes red-blink {
                    0%, 100% {
                        opacity: 0.5;
                    }
                    10%, 30%, 50%, 70%, 90% {
                        opacity: 0.8;
                    }
                    20%, 40%, 60%, 80% {
                        opacity: 0.95;
                    }
                }
                
                @keyframes points-float {
                    0% {
                        transform: translate(-50%, -50%) scale(0.3);
                        opacity: 0;
                    }
                    20% {
                        transform: translate(-50%, -60%) scale(1.3);
                        opacity: 1;
                    }
                    50% {
                        transform: translate(-50%, -100%) scale(1.1);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -180%) scale(0.9);
                        opacity: 0;
                    }
                }
                
                /* Larger cursor for clickable emojis */
                .emoji-clickable {
                    cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24' fill='none' stroke='%231890ff' stroke-width='3'%3E%3Cpath d='M12 2v20M2 12h20'/%3E%3C/svg%3E") 16 16, crosshair !important;
                }
                
                .emoji-clickable:hover {
                    cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='none' stroke='%2352c41a' stroke-width='4'%3E%3Cpath d='M12 2v20M2 12h20'/%3E%3C/svg%3E") 20 20, crosshair !important;
                }
                
                /* Game area cursor */
                .game-area-container {
                    cursor: crosshair;
                }
                
                @keyframes catch-emoji-pop {
                    0% {
                        transform: translate(-50%, -50%) scale(1) rotate(0deg);
                        opacity: 1;
                    }
                    30% {
                        transform: translate(-50%, -50%) scale(1.2) rotate(180deg);
                        opacity: 1;
                    }
                    60% {
                        transform: translate(-50%, -50%) scale(1.4) rotate(360deg);
                        opacity: 0.8;
                    }
                    100% {
                        transform: translate(-50%, -50%) scale(1.6) rotate(540deg);
                        opacity: 0;
                    }
                }
                
                @keyframes burst-ring-expand {
                    0% {
                        width: 0;
                        height: 0;
                        opacity: 1;
                    }
                    50% {
                        width: 100px;
                        height: 100px;
                        opacity: 0.8;
                    }
                    100% {
                        width: 180px;
                        height: 180px;
                        opacity: 0;
                    }
                }
                
                @keyframes explosion-pop {
                    0% {
                        transform: translate(-50%, -50%) scale(0.5) rotate(0deg);
                        opacity: 1;
                    }
                    40% {
                        transform: translate(-50%, -50%) scale(1.2) rotate(180deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) scale(1.5) rotate(360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes explosion-ring-expand {
                    0% {
                        width: 0;
                        height: 0;
                        opacity: 1;
                    }
                    50% {
                        width: 120px;
                        height: 120px;
                        opacity: 0.9;
                    }
                    100% {
                        width: 200px;
                        height: 200px;
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-1 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(-80px) translateY(-80px) scale(1.5) rotate(360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-2 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(80px) translateY(-80px) scale(1.5) rotate(-360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-3 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(-80px) translateY(80px) scale(1.5) rotate(360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-4 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(80px) translateY(80px) scale(1.5) rotate(-360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-5 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(0) translateY(-100px) scale(1.5) rotate(360deg);
                        opacity: 0;
                    }
                }
                
                @keyframes particle-burst-6 {
                    0% {
                        transform: translate(-50%, -50%) translateX(0) translateY(0) scale(0) rotate(0deg);
                        opacity: 1;
                    }
                    100% {
                        transform: translate(-50%, -50%) translateX(0) translateY(100px) scale(1.5) rotate(-360deg);
                        opacity: 0;
                    }
                }
                
                .catch-animation {
                    transform: translate(-50%, -50%);
                    transform-origin: center center;
                }
                
                .catch-blast {
                    position: relative;
                    width: 250px;
                    height: 250px;
                }
                
                .catch-emoji {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    font-size: 60px;
                    animation: catch-emoji-pop 1s ease-out forwards;
                    z-index: 2;
                    filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.8));
                }
                
                .burst-ring {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 0;
                    height: 0;
                    border: 3px solid rgba(255, 255, 255, 0.8);
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    animation: burst-ring-expand 1s ease-out forwards;
                    z-index: 1;
                }
                
                .catch-particles {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 200px;
                    height: 200px;
                }
                
                .catch-particles span {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    font-size: 36px;
                }
                
                .explosion-blast {
                    position: relative;
                    width: 220px;
                    height: 220px;
                }
                
                .explosion-emoji {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    font-size: 65px;
                    animation: explosion-pop 1s ease-out forwards;
                    z-index: 2;
                    filter: drop-shadow(0 0 15px rgba(255, 0, 0, 0.9));
                }
                
                .explosion-ring {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 0;
                    height: 0;
                    border: 4px solid rgba(255, 0, 0, 0.9);
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    animation: explosion-ring-expand 1s ease-out forwards;
                    z-index: 1;
                }
                
                .explosion-particles {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    width: 220px;
                    height: 220px;
                }
                
                .explosion-particles span {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    font-size: 40px;
                }
                
                .particle-1 {
                    animation: particle-burst-1 0.8s ease-out forwards;
                }
                .particle-2 {
                    animation: particle-burst-2 0.8s ease-out forwards;
                }
                .particle-3 {
                    animation: particle-burst-3 0.8s ease-out forwards;
                }
                .particle-4 {
                    animation: particle-burst-4 0.8s ease-out forwards;
                }
                .particle-5 {
                    animation: particle-burst-5 0.8s ease-out forwards;
                }
                .particle-6 {
                    animation: particle-burst-6 0.8s ease-out forwards;
                }
                .particle-7 {
                    animation: particle-burst-7 1s ease-out forwards;
                }
                .particle-8 {
                    animation: particle-burst-8 1s ease-out forwards;
                }
                
                .exp-particle-1 {
                    animation: exp-particle-burst-1 1s ease-out forwards;
                }
                .exp-particle-2 {
                    animation: exp-particle-burst-2 1s ease-out forwards;
                }
                .exp-particle-3 {
                    animation: exp-particle-burst-3 1s ease-out forwards;
                }
                .exp-particle-4 {
                    animation: exp-particle-burst-4 1s ease-out forwards;
                }
                .exp-particle-5 {
                    animation: exp-particle-burst-5 1s ease-out forwards;
                }
                .exp-particle-6 {
                    animation: exp-particle-burst-6 1s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default LoadingGame;

